import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useCotizacionItems } from "@/hooks/useCotizacionContenido";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import {
  guardarCotizacionItemRequest,
  guardarCotizacionTextoRequest,
  listarCotizacionTextosRequest,
  listarMarcadoresRequest,
  ROTULO_APARTADO,
  type CotizacionItem,
  type CotizacionTexto,
} from "@/lib/cotizacionContenido";

/**
 * El contenido del documento de cotización, editable sin desplegar.
 *
 * POR QUÉ EXISTE. Lo que más se itera de este documento es su redacción, y
 * quien la itera no es quien despliega: un "abono" que pasa a decir "anticipo"
 * no puede necesitar un release. Es el mismo argumento que ya se aceptó para
 * los prompts en el Mantenedor de IA, y esta pantalla sigue su forma —
 * maestro-detalle con `Textarea`, porque un párrafo no se lee en una celda.
 *
 * DOS PESTAÑAS Y NO DOS PANTALLAS: son las dos mitades del mismo documento y
 * quien viene a corregir la metodología suele mirar también las condiciones de
 * pago. Con un solo destino, el menú lo declara como fila plana.
 */
export function MantenedorDeCotizacion() {
  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <p className="text-ink-muted max-w-[70ch] text-sm">
        Lo que se edita acá cambia lo que dicen TODAS las cotizaciones que se emitan
        después. Las ya enviadas no se tocan: su documento quedó congelado al generarse.
      </p>

      <Tabs defaultValue="textos" variant="underline">
        <TabsList>
          <TabsTrigger value="textos">Textos fijos</TabsTrigger>
          <TabsTrigger value="items">Metodología y documentación</TabsTrigger>
        </TabsList>

        <TabsContent value="textos" className="pt-5">
          <PestanaDeTextos />
        </TabsContent>

        <TabsContent value="items" className="pt-5">
          <PestanaDeItems />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** Los párrafos fijos, con la lista de marcadores a la vista. */
function PestanaDeTextos() {
  const queryClient = useQueryClient();
  const textos = useQuery({ queryKey: ["cotizacion-textos"], queryFn: listarCotizacionTextosRequest });
  const marcadores = useQuery({
    queryKey: ["cotizacion-marcadores"],
    queryFn: listarMarcadoresRequest,
    // Los marcadores los fija el código, no la base: no cambian en una sesión.
    staleTime: Infinity,
  });

  const [elegido, setElegido] = useState<number | null>(null);
  const [cuerpo, setCuerpo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: (texto: CotizacionTexto) =>
      guardarCotizacionTextoRequest(texto.id, { nombre: texto.nombre, cuerpo }),
    onSuccess: () => {
      setError(null);
      toast.success("Texto guardado.");
      void queryClient.invalidateQueries({ queryKey: ["cotizacion-textos"] });
    },
    onError: (fallo) => {
      // El 422 del marcador inventado llega por acá y hay que MOSTRARLO, no
      // solo registrarlo: es el único aviso de que la llave está mal escrita
      // antes de que el documento se rompa al imprimirse.
      const validacion = parseValidationErrors(fallo);
      setError(validacion?.cuerpo ?? getApiErrorMessage(fallo, "No pudimos guardar el cambio."));
    },
  });

  if (textos.isPending) return <Skeleton className="h-64 w-full rounded-xl" />;
  if (textos.isError || !textos.data) {
    return <p className="text-destructive text-sm">No pudimos cargar los textos.</p>;
  }

  const seleccionado = textos.data.find((texto) => texto.id === elegido) ?? null;

  return (
    <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
      <ul className="divide-border bg-card divide-y overflow-hidden rounded-xl border">
        {textos.data.map((texto) => (
          <li key={texto.id}>
            <button
              type="button"
              className={`hover:bg-muted/50 w-full px-4 py-3 text-left ${
                texto.id === elegido ? "bg-muted" : ""
              }`}
              onClick={() => {
                setElegido(texto.id);
                setCuerpo(texto.cuerpo);
                setError(null);
              }}
            >
              <p className="text-ink text-body font-bold">{texto.nombre}</p>
              <p className="text-ink-hint text-caption font-mono">{texto.codigo}</p>
            </button>
          </li>
        ))}
      </ul>

      {seleccionado ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cuerpo-del-texto">{seleccionado.nombre}</Label>
            <Textarea
              id="cuerpo-del-texto"
              rows={10}
              value={cuerpo}
              onChange={(evento) => setCuerpo(evento.target.value)}
              aria-invalid={Boolean(error)}
            />
            {error ? <p className="text-destructive text-caption">{error}</p> : null}
          </div>

          {/* Los marcadores A LA VISTA mientras se edita. Sin esto hay que
              adivinar las claves, y adivinar es exactamente lo que produce el
              typo que el vocabulario cerrado viene a evitar. */}
          <div className="space-y-2">
            <Label>Marcadores disponibles</Label>
            <div className="flex flex-wrap gap-2">
              {(marcadores.data ?? []).map((marcador) => (
                <Badge key={marcador.clave} variant="info" title={marcador.ayuda}>
                  {`{{${marcador.clave}}}`}
                </Badge>
              ))}
            </div>
          </div>

          <Button onClick={() => guardar.mutate(seleccionado)} disabled={guardar.isPending}>
            {guardar.isPending ? <Spinner /> : null} Guardar
          </Button>
        </div>
      ) : (
        <p className="text-ink-muted text-caption">Elegí un texto de la lista para editarlo.</p>
      )}
    </div>
  );
}

/** El catálogo de ítems de Metodología y Documentación. */
function PestanaDeItems() {
  const queryClient = useQueryClient();
  const items = useCotizacionItems();

  const [elegido, setElegido] = useState<number | null>(null);
  const [rotulo, setRotulo] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: (item: CotizacionItem) =>
      guardarCotizacionItemRequest(item.id, {
        // `""` vuelve a ser `null`: los de Documentación no llevan rótulo, y
        // guardar una cadena vacía imprimiría un ":" suelto.
        rotulo: rotulo.trim() === "" ? null : rotulo.trim(),
        cuerpo,
        orden: item.orden,
      }),
    onSuccess: () => {
      setError(null);
      toast.success("Ítem guardado.");
      void queryClient.invalidateQueries({ queryKey: ["cotizacion-items"] });
    },
    onError: (fallo) => {
      const validacion = parseValidationErrors(fallo);
      setError(validacion?.cuerpo ?? getApiErrorMessage(fallo, "No pudimos guardar el cambio."));
    },
  });

  if (items.isPending) return <Skeleton className="h-64 w-full rounded-xl" />;
  if (items.isError || !items.data) {
    return <p className="text-destructive text-sm">No pudimos cargar el catálogo.</p>;
  }

  const seleccionado = items.data.find((item) => item.id === elegido) ?? null;

  return (
    <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
      <ul className="divide-border bg-card divide-y overflow-hidden rounded-xl border">
        {items.data.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={`hover:bg-muted/50 w-full px-4 py-3 text-left ${
                item.id === elegido ? "bg-muted" : ""
              }`}
              onClick={() => {
                setElegido(item.id);
                setRotulo(item.rotulo ?? "");
                setCuerpo(item.cuerpo);
                setError(null);
              }}
            >
              <p className="text-ink text-body font-bold">{item.rotulo ?? item.codigo}</p>
              <p className="text-ink-hint text-caption">{ROTULO_APARTADO[item.apartado]}</p>
            </button>
          </li>
        ))}
      </ul>

      {seleccionado ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rotulo-del-item">Rótulo</Label>
            <Input
              id="rotulo-del-item"
              value={rotulo}
              placeholder="Vacío en los ítems de documentación"
              onChange={(evento) => setRotulo(evento.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cuerpo-del-item">Texto</Label>
            <Textarea
              id="cuerpo-del-item"
              rows={8}
              value={cuerpo}
              onChange={(evento) => setCuerpo(evento.target.value)}
              aria-invalid={Boolean(error)}
            />
            {error ? <p className="text-destructive text-caption">{error}</p> : null}
          </div>

          <Button onClick={() => guardar.mutate(seleccionado)} disabled={guardar.isPending}>
            {guardar.isPending ? <Spinner /> : null} Guardar
          </Button>
        </div>
      ) : (
        <p className="text-ink-muted text-caption">Elegí un ítem de la lista para editarlo.</p>
      )}
    </div>
  );
}
